import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { trackFootfallEvent } from 'src/utils/footfall';
import EventType from 'src/constants/footfallEventTypes';
import {
  isUniqueImpression,
  addToUniqueImpressionsList,
  formatLink,
  getModalParams,
  getCtaType,
  getHelpScoutArticle,
  addToDismissList,
} from 'src/utils/featuredPosts';
import { Featured } from 'src/types';
import { handleQueryParams } from 'src/modules/shared/modal/actions';
import { useHelpScout } from 'src/utils/hooks';
import NormalFeaturedPost from './normalFeaturedPost';
import StickyFeaturedPost from './stickyFeaturedPost';
import DismissFeedback from './dismissFeedback';

type Props = {
  featuredPost: Featured;
  isSticky?: boolean;
};

export default function FeaturedPost({ featuredPost, isSticky = false }: Props) {
  const dispatch = useDispatch();
  const [isDismissed, setIsDismissed] = useState(false);

  const { featuredPostId, callToActionUrl } = featuredPost;
  const formattedLink = formatLink(callToActionUrl);

  useEffect(() => {
    trackFootfallEvent(EventType.VIEWED_FEATURED_POST, { featuredPostId });

    if (isUniqueImpression(featuredPostId)) {
      trackFootfallEvent(EventType.VIEWED_FEATURED_POST_UNIQUE, { featuredPostId });
      addToUniqueImpressionsList(featuredPostId);
    }
  }, [featuredPostId]);

  const onDismiss = () => {
    setIsDismissed(true);
    addToDismissList(featuredPostId);
    trackFootfallEvent(EventType.DISMISSED_FEATURED_POST, { featuredPostId });
  };

  const openModal = (link: string) => {
    const params = getModalParams(link);
    if (params) {
      const footfallSection = 'Campaign';
      dispatch(handleQueryParams(params, footfallSection));
    }
  };
  const openHelpScout = (url: string) => {
    const article = getHelpScoutArticle(url, featuredPostId);
    if (article) useHelpScout(article, { type: 'modal' });
  };

  const onClick = (e: SyntheticEvent) => {
    e.preventDefault();
    trackFootfallEvent(EventType.CLICKED_FEATURED_POST, { featuredPostId });
    const ctaType = getCtaType(callToActionUrl);
    switch (ctaType) {
      case 'modal':
        openModal(callToActionUrl);
        break;
      case 'helpscout':
        openHelpScout(callToActionUrl);
        break;
      case 'mailto':
        window.location.href = callToActionUrl;
        break;
      case 'oldModal':
        openModal(callToActionUrl);
        break;
      case 'link':
      default:
        window.open(formattedLink);
    }
  };

  return (
    <Fragment>
      {isDismissed
        ? <DismissFeedback featuredPostId={featuredPostId} />
        : isSticky
          ? <StickyFeaturedPost featuredPost={featuredPost} onClick={onClick} onDismiss={onDismiss} />
          : <NormalFeaturedPost featuredPost={featuredPost} onClick={onClick} onDismiss={onDismiss} />
      }
    </Fragment>
  );
}
